import { Injectable } from '@angular/core'
import { BehaviorSubject, Subscription } from 'rxjs'
import { UiStateService } from '../../services/ui-state.service'
import { Application } from './application.service'
import { CrudAction } from 'src/utils/crud-subject'

interface OnboardingState {
  applicationCreated: boolean
  accountSettingsVisited: {
    general: boolean
    retention: boolean
    identityProviders: boolean
    apiKeys: boolean
  }
  accessManagementVisited: {
    users: boolean
    roles: boolean
  }
  introDismissed: boolean
}

const DEFAULT_STATE: OnboardingState = {
  applicationCreated: false,
  accountSettingsVisited: {
    general: false,
    retention: false,
    identityProviders: false,
    apiKeys: false,
  },
  accessManagementVisited: {
    users: false,
    roles: false,
  },
  introDismissed: false,
}

// Keys used in the backend uiState API
const UI_STATE_KEYS = {
  ACCOUNT_SETTINGS_GENERAL: 'onboarding.accountSettings.general',
  ACCOUNT_SETTINGS_RETENTION: 'onboarding.accountSettings.retention',
  ACCOUNT_SETTINGS_IDENTITY_PROVIDERS: 'onboarding.accountSettings.identityProviders',
  ACCOUNT_SETTINGS_API_KEYS: 'onboarding.accountSettings.apiKeys',
  ACCESS_MANAGEMENT_USERS: 'onboarding.accessManagement.users',
  ACCESS_MANAGEMENT_ROLES: 'onboarding.accessManagement.roles',
  INTRO_DISMISSED: 'onboarding.introDismissed',
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private state: OnboardingState
  private stateSubject: BehaviorSubject<OnboardingState>
  private currentAccountId: string | null = null
  private saveInProgress = false
  private pendingSave = false
  private applicationSubscription: Subscription | null = null
  private _hasApplications = false
  private _loaded = false
  private _hasPersistedOnboardingState = false

  constructor(private uiStateService: UiStateService) {
    this.state = { ...DEFAULT_STATE }
    this.stateSubject = new BehaviorSubject<OnboardingState>(this.state)
  }

  setAccountId(accountId: string): void {
    if (this.currentAccountId !== accountId) {
      this.currentAccountId = accountId
      this.loadStateFromBackend()
      this.subscribeToApplications(accountId)
    }
  }

  private subscribeToApplications(accountId: string): void {
    // Clean up any previous subscription
    if (this.applicationSubscription) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }

    // Track applications by subscribing to the static Application.publishSubject$
    const applicationIds = new Set<string>()

    // Subscribe to application CRUD events
    this.applicationSubscription = Application.publishSubject$.subscribe({
      next: (crudEvent) => {
        // Only track applications for the current account
        if (crudEvent.entity && crudEvent.entity.accountId !== accountId) {
          return
        }

        if (crudEvent.action === CrudAction.UPSERT && crudEvent.entity && !crudEvent.entity.deleted) {
          applicationIds.add(crudEvent.entityId)
        } else if (crudEvent.action === CrudAction.DELETE) {
          applicationIds.delete(crudEvent.entityId)
        } else if (crudEvent.action === CrudAction.UPSERT && crudEvent.entity?.deleted) {
          // Handle soft deletes
          applicationIds.delete(crudEvent.entityId)
        }

        const hadApplications = this._hasApplications
        this._hasApplications = applicationIds.size > 0
        if (hadApplications !== this._hasApplications) {
          this.emitCurrentState()
        }
      },
    })
  }

  private emitCurrentState(): void {
    // Update the state's applicationCreated to reflect actual application existence
    this.state.applicationCreated = this._hasApplications
    this.stateSubject.next({ ...this.state })
  }

  private loadStateFromBackend(): void {
    if (!this.currentAccountId) return

    this._loaded = false
    this._hasPersistedOnboardingState = false
    this.uiStateService.load(this.currentAccountId).subscribe({
      next: (uiState) => {
        if (uiState == null) {
          // Null uiState (never saved) - treat as already onboarded
          this.state = { ...DEFAULT_STATE, introDismissed: true }
        } else {
          const hasOnboardingKeys = Object.values(UI_STATE_KEYS).some((key) => key in uiState)
          this._hasPersistedOnboardingState = hasOnboardingKeys
          this.state = hasOnboardingKeys ? this.uiStateToOnboardingState(uiState) : { ...DEFAULT_STATE }
        }
        this._loaded = true
        this.stateSubject.next({ ...this.state })
      },
      error: (err) => {
        console.warn('Failed to load onboarding state from backend, using defaults:', err)
        this.state = { ...DEFAULT_STATE }
        this._loaded = true
        this.stateSubject.next({ ...this.state })
      },
    })
  }

  private uiStateToOnboardingState(uiState: Record<string, string>): OnboardingState {
    return {
      // applicationCreated is now computed from actual applications, not persisted
      applicationCreated: this._hasApplications,
      accountSettingsVisited: {
        general: uiState[UI_STATE_KEYS.ACCOUNT_SETTINGS_GENERAL] === 'true',
        retention: uiState[UI_STATE_KEYS.ACCOUNT_SETTINGS_RETENTION] === 'true',
        identityProviders: uiState[UI_STATE_KEYS.ACCOUNT_SETTINGS_IDENTITY_PROVIDERS] === 'true',
        apiKeys: uiState[UI_STATE_KEYS.ACCOUNT_SETTINGS_API_KEYS] === 'true',
      },
      accessManagementVisited: {
        users: uiState[UI_STATE_KEYS.ACCESS_MANAGEMENT_USERS] === 'true',
        roles: uiState[UI_STATE_KEYS.ACCESS_MANAGEMENT_ROLES] === 'true',
      },
      introDismissed: uiState[UI_STATE_KEYS.INTRO_DISMISSED] === 'true',
    }
  }

  private onboardingStateToUiState(state: OnboardingState): Record<string, string> {
    // applicationCreated is computed from actual applications, not persisted
    return {
      [UI_STATE_KEYS.ACCOUNT_SETTINGS_GENERAL]: String(state.accountSettingsVisited.general),
      [UI_STATE_KEYS.ACCOUNT_SETTINGS_RETENTION]: String(state.accountSettingsVisited.retention),
      [UI_STATE_KEYS.ACCOUNT_SETTINGS_IDENTITY_PROVIDERS]: String(state.accountSettingsVisited.identityProviders),
      [UI_STATE_KEYS.ACCOUNT_SETTINGS_API_KEYS]: String(state.accountSettingsVisited.apiKeys),
      [UI_STATE_KEYS.ACCESS_MANAGEMENT_USERS]: String(state.accessManagementVisited.users),
      [UI_STATE_KEYS.ACCESS_MANAGEMENT_ROLES]: String(state.accessManagementVisited.roles),
      [UI_STATE_KEYS.INTRO_DISMISSED]: String(state.introDismissed),
    }
  }

  private saveState(): void {
    this._hasPersistedOnboardingState = true
    this.stateSubject.next({ ...this.state })

    if (!this.currentAccountId) return

    // Debounce saves to avoid too many API calls
    if (this.saveInProgress) {
      this.pendingSave = true
      return
    }

    this.saveInProgress = true
    const onboardingState = this.onboardingStateToUiState(this.state)

    this.uiStateService.save(this.currentAccountId, onboardingState).subscribe({
      next: () => this.handleSaveComplete(),
      error: (err) => {
        console.error('Failed to save onboarding state to backend:', err)
        this.handleSaveComplete()
      },
    })
  }

  private handleSaveComplete(): void {
    this.saveInProgress = false
    if (this.pendingSave) {
      this.pendingSave = false
      this.saveState()
    }
  }

  get state$() {
    return this.stateSubject.asObservable()
  }

  get loaded(): boolean {
    return this._loaded
  }

  get hasPersistedOnboardingState(): boolean {
    return this._hasPersistedOnboardingState
  }

  // Application created check - computed from actual applications, not a persisted flag
  get applicationCreated(): boolean {
    return this._hasApplications
  }

  // Account settings tabs visited
  markAccountSettingsTabVisited(tab: 'general' | 'retention' | 'identityProviders' | 'apiKeys'): void {
    this.state.accountSettingsVisited[tab] = true
    this.saveState()
  }

  get accountSettingsComplete(): boolean {
    const visited = this.state.accountSettingsVisited
    // User only needs to visit the page (any tab) to complete this check
    return visited.general || visited.retention || visited.identityProviders || visited.apiKeys
  }

  toggleAccountSettingsComplete(): void {
    const newValue = !this.accountSettingsComplete
    this.state.accountSettingsVisited = {
      general: newValue,
      retention: newValue,
      identityProviders: newValue,
      apiKeys: newValue,
    }
    this.saveState()
  }

  // Access management tabs visited
  markAccessManagementTabVisited(tab: 'users' | 'roles'): void {
    this.state.accessManagementVisited[tab] = true
    this.saveState()
  }

  get accessManagementComplete(): boolean {
    const visited = this.state.accessManagementVisited
    // User only needs to visit the page (any tab) to complete this check
    return visited.users || visited.roles
  }

  toggleAccessManagementComplete(): void {
    const newValue = !this.accessManagementComplete
    this.state.accessManagementVisited = {
      users: newValue,
      roles: newValue,
    }
    this.saveState()
  }

  // Intro dismissed
  get introDismissed(): boolean {
    return this.state.introDismissed
  }

  dismissIntro(): void {
    this.state.introDismissed = true
    this.saveState()
  }

  // All checks complete
  get allChecksComplete(): boolean {
    return this.applicationCreated && this.accountSettingsComplete && this.accessManagementComplete
  }

  // Reset state (useful for testing)
  resetState(): void {
    this.state = { ...DEFAULT_STATE }
    this.saveState()
  }
}
